'use client';

import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker, getDefaultClassNames, type DayPickerLocale } from 'react-day-picker';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';

import 'react-day-picker/style.css';

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  locale?: DayPickerLocale;
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  locale,
  ...props
}: CalendarProps): React.JSX.Element {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={locale}
      className={cn('p-3', className)}
      classNames={{
        ...defaultClassNames,
        root: cn('rounded-md border bg-background', defaultClassNames.root),
        nav: cn('flex items-center gap-1', defaultClassNames.nav),
        button_previous: cn(
          buttonVariants({ variant: 'outline', size: 'icon' }),
          'absolute left-1 top-1 size-7 bg-transparent opacity-50 hover:opacity-100',
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline', size: 'icon' }),
          'absolute right-1 top-1 size-7 bg-transparent opacity-50 hover:opacity-100',
          defaultClassNames.button_next
        ),
        day_button: cn(
          buttonVariants({ variant: 'ghost', size: 'icon' }),
          'size-8 font-normal aria-selected:opacity-100',
          defaultClassNames.day_button
        ),
        selected: 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        today: cn('bg-accent text-accent-foreground', defaultClassNames.today),
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, ...chevronProps }) => {
          if (orientation === 'left') {
            return <ChevronLeft className="size-4" {...chevronProps} />;
          }
          return <ChevronRight className="size-4" {...chevronProps} />;
        },
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
